import { Icon, icons } from '@agentisai/design-system';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import {
  activeIndexVar,
  current as currentCls,
  done as doneCls,
  heldTitle as heldTitleCls,
  indicator as indicatorCls,
  list as listCls,
  root as rootCls,
  step as stepCls,
  stepCountVar,
  stepLabel as stepLabelCls,
  track as trackCls,
  upcoming as upcomingCls,
} from './CreateProgress.css';

/**
 * Where the create consultation has got to. Derived by the parent from the two
 * signals the pre-create chat response already carries — no `workstreamTitle`
 * yet / title held / `designConfirmed` latched.
 *
 * ponytail: three steps is the ceiling of what the wire can PROVE, not a claim
 * that the interview has three phases (the agent skill runs five). Upgrade path:
 * one additive-optional `phase` field, then widen `CreateStage` + `STEPS`
 * together — the fill's geometry reads `STEPS.length`, never a hard-coded 3.
 */
export type CreateStage = 'understanding' | 'designing' | 'confirmed';

const STEPS: readonly { stage: CreateStage; label: string; icon: typeof icons.Search }[] = [
  { stage: 'understanding', label: 'Understand', icon: icons.Search },
  { stage: 'designing', label: 'Design', icon: icons.Layers },
  { stage: 'confirmed', label: 'Confirm', icon: icons.CircleCheck },
];

export interface CreateProgressProps {
  /** Current stage, derived by the parent from the wire signals. */
  readonly stage: CreateStage;
  /** The agent-proposed title, once held. Shown beneath the pill. */
  readonly title?: string | null;
}

/**
 * CreateProgress — the three-step segmented pill for the create consultation,
 * rendered in `CreateWorkstreamPanel`'s `ChatPanel` caption slot. One-off page
 * composition (frontend-architecture R0), not a DS component. Read-only: there
 * is nothing to navigate to.
 */
export function CreateProgress({ stage, title }: CreateProgressProps) {
  const activeIndex = STEPS.findIndex((s) => s.stage === stage);
  return (
    <div className={rootCls}>
      <div
        className={trackCls}
        style={assignInlineVars({
          [stepCountVar]: String(STEPS.length),
          [activeIndexVar]: String(activeIndex),
        })}
      >
        {/* Decorative — `aria-current` below carries progress to a SR. */}
        <div className={indicatorCls} aria-hidden="true" />
        <ol className={listCls} aria-label="Workstream setup progress">
          {STEPS.map((s, i) => (
            <li
              key={s.stage}
              // The only navigational semantic here; done/upcoming is colour.
              aria-current={i === activeIndex ? 'step' : undefined}
              className={[
                stepCls,
                i < activeIndex ? doneCls : i === activeIndex ? currentCls : upcomingCls,
              ].join(' ')}
            >
              <Icon as={s.icon} size={14} />
              <span className={stepLabelCls}>{s.label}</span>
            </li>
          ))}
        </ol>
      </div>
      {title && <p className={heldTitleCls}>{title}</p>}
    </div>
  );
}
CreateProgress.displayName = 'CreateProgress';
